import React, { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { BonusCampaignsDto } from 'api/Wallet/Types/MarketingBonusCampaignDto'
import { FilterType } from 'components/Filter'
import { getBonusCampaignFilterFields } from 'components/Filter/BonusCampaignFields/bonusCampaignFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { Spinner } from 'components/Spinner'
import { useGridStatusState } from 'features/DataGrid/hooks'
import uniqBy from 'lodash/uniqBy'
import { useAppSelector } from 'store'
import { GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery } from 'utils/QueryHelpers'

export interface SelectBonusCampaignEvent {
  filterId?: string
  value: BonusCampaignsDto[]
}

interface SelectBonusCampaignTableProps {
  filterId?: string
  onSelect: (event: SelectBonusCampaignEvent) => void
  onClose: () => void
}

interface CampaignWithSelection extends BonusCampaignsDto {
  selected: boolean
}

export const SelectBonusCampaignTable: React.FC<SelectBonusCampaignTableProps> = ({
  filterId,
  onSelect,
  onClose,
}) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const filters = useAppSelector((state) => state.identity.filter)
  const { gridStatusState, gridStatusActions } = useGridStatusState()

  const [campaigns, setCampaigns] = useState<CampaignWithSelection[]>([])
  const [selectedCampaigns, setSelectedCampaigns] = useState<BonusCampaignsDto[]>([])
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )

  const getQuery = useCallback((filter?: CompositeFilterDescriptor | null): string | null => {
    return filter ? buildQuery(filter) : null
  }, [])

  const fetchCampaigns = useCallback(
    async (params?: {
      query?: string | null
      filter?: CompositeFilterDescriptor | null
      pageIndex?: number
      pageSize?: number
    }) => {
      gridStatusActions.setLoading(true)
      try {
        const searchQuery =
          params?.query !== undefined ? params.query : getQuery(gridFilterState.filter)
        const pageIndex = params?.pageIndex ?? gridFilterState.pageIndex ?? 1
        const pageSize = (params?.pageSize ?? gridFilterState.pageSize) || 10

        const response = await walletsApiClient.getBonusCampaigns({
          search: searchQuery,
          pageIndex,
          pageSize,
        })

        if (response?.items) {
          const campaignsWithSelection = response.items.map((campaign: BonusCampaignsDto) => {
            const isSelected = selectedCampaigns.some((selected) => selected.id === campaign.id)
            return { ...campaign, selected: isSelected }
          })
          setCampaigns(campaignsWithSelection)
        }

        const total = response.totalResults
        const skip = ((pageIndex ?? 1) - 1) * pageSize
        const take = pageSize

        setGridFilterState((prev) => ({
          ...prev,
          total,
          skip,
          take,
          pageIndex,
          pageSize,
          filter: params?.filter ?? prev.filter,
        }))
      } catch (error) {
        console.error('Error fetching campaigns:', error)
      } finally {
        gridStatusActions.setLoading(false)
      }
    },
    [walletsApiClient, getQuery, gridFilterState.filter, selectedCampaigns]
  )

  useEffect(() => {
    const loadInitialData = async () => {
      if (filterId) {
        const savedFilter = filters.find((filter) => filter.id === filterId)

        if (savedFilter) {
          try {
            const filterData = JSON.parse(savedFilter.data)
            if (filterData.selectedCampaigns) {
              setSelectedCampaigns(filterData.selectedCampaigns)
            }
            if (filterData.filter) {
              setGridFilterState((prev) => ({
                ...prev,
                filter: filterData.filter,
              }))
            }
          } catch (error) {
            console.error('Error parsing saved filter:', error)
          }
        }
      }
      await fetchCampaigns()
    }

    loadInitialData()
  }, [])

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const campaign = event.dataItem as CampaignWithSelection

    if (!campaign.selected) {
      setSelectedCampaigns((prev) => [...prev, campaign])
    } else {
      setSelectedCampaigns((prev) => prev.filter((x) => x.id !== campaign.id))
    }

    setCampaigns((prev) =>
      prev.map((item) =>
        item.id === campaign.id ? { ...item, selected: !campaign.selected } : item
      )
    )
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const isChecked = event.syntheticEvent.target.checked

    setCampaigns((prev) => prev.map((campaign) => ({ ...campaign, selected: isChecked })))

    if (isChecked) {
      setSelectedCampaigns((prev) => uniqBy([...prev, ...campaigns], 'id'))
    } else {
      setSelectedCampaigns((prev) =>
        prev.filter((selected) => !campaigns.some((campaign) => campaign.id === selected.id))
      )
    }
  }

  const handlePageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pageIndex = skip / take + 1

    setGridFilterState((prev) => ({
      ...prev,
      pageIndex,
      pageSize: take,
      skip,
      take,
    }))

    await fetchCampaigns({ pageIndex, pageSize: take })
  }

  const handleApply = () => {
    onSelect({ filterId, value: uniqBy(selectedCampaigns, 'id') })
    onClose()
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <div style={{ margin: 16 }}>
      <h3>Select Campaigns</h3>
      <SelectFiltersFacade
        gridState={gridFilterState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
        filterFetch={(params) => fetchCampaigns(params)}
        filterType={FilterType.BonusCampaignOverview}
        filterFields={getBonusCampaignFilterFields()}
        containerClassName='campaign-table__filter'
        getQuery={getQuery}
        showFilterWhenInactive
      />
      <Grid
        data={campaigns}
        selectedField='selected'
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={handlePageChange}
        pageable={pageableSettings}
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        style={{ height: '400px' }}
      >
        <Column
          field='selected'
          width='50px'
          headerSelectionValue={campaigns.findIndex((dataItem) => !dataItem.selected) === -1}
        />
        <Column field='name' title='Campaign Name' />
        <Column
          field='bonusCampaignStatus.name'
          title='Status'
          cell={(props: GridCellProps) => (
            <td>{props.dataItem.bonusCampaignStatus?.name || 'N/A'}</td>
          )}
        />
        <Column
          field='startDate'
          title='Start Date'
          cell={(props: GridCellProps) => (
            <td>
              {props.dataItem.startDate
                ? new Date(props.dataItem.startDate).toLocaleDateString()
                : 'N/A'}
            </td>
          )}
        />
        <Column
          field='endDate'
          title='End Date'
          cell={(props: GridCellProps) => (
            <td>
              {props.dataItem.endDate
                ? new Date(props.dataItem.endDate).toLocaleDateString()
                : 'N/A'}
            </td>
          )}
        />
        <Column
          field='bonusType.name'
          title='Bonus Type'
          cell={(props: GridCellProps) => <td>{props.dataItem.bonusType?.name || 'N/A'}</td>}
        />
      </Grid>
      <Button onClick={handleApply} disabled={selectedCampaigns.length === 0}>
        Save
      </Button>
    </div>
  )
}
